import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { delAuth } from '@/api/ApiCalls';
import { FundGroupDtoIn } from '@/models/in/FundGroupDtoIn';

interface DeleteFundGroupDialogProps {
  open: boolean;
  group: FundGroupDtoIn;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteFundGroupDialog = ({ open, group, onClose, onSuccess }: DeleteFundGroupDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await delAuth(`management/fund-groups/${group.id}`, headers);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      setError(message.includes('used by') || group.usedByFeaturesCount > 0
        ? t('fundGroups.dialog.inUseError', { count: group.usedByFeaturesCount })
        : t('fundGroups.dialog.deleteError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }} title={t('fundGroups.dialog.deleteTitle')}>
      <div className="flex flex-col gap-4 p-4 min-w-[420px]">
        <p className="body-sm-regular">{t('fundGroups.dialog.deleteConfirm', { name: group.name })}</p>
        {error && <p className="text-red-500 body-sm-regular">{error}</p>}
        <div className="flex gap-3 justify-end mt-2">
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>{t('close')}</Button>
          <Button variant="primary" className="!bg-red-500" onClick={handleDelete} disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('fundGroups.delete')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default DeleteFundGroupDialog;
